import { erpApi } from "@/shared/axios";
import { urlBackend } from "@/shared/constants";
import { 
  ReporteVentaPagingRes, 
  ReporteVentaParams,
  ReporteGeneralVentasParams,
  ReporteGeneralVentasResponse 
} from "@/shared/interfaces";
import { getUrlParamsFetch } from "@/shared/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const { get, post } = erpApi();

// Hook original - mantener para compatibilidad
export const useFetchReporteGeneral = (params?: ReporteVentaParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['buscador-general', ...Object.values(params || {})],
    queryFn: async () => {
      return await getBuscadorGeneral(params)
    },
    retry: false,
    enabled: enabled
  })
}

export const getBuscadorGeneral = async (params?: ReporteVentaParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = get<ReporteVentaPagingRes>(
    `${urlBackend}api/v1/buscador-general/?${queryParams}`,
    true
  );
  return resp;
};

// ==================== REPORTE GENERAL DE VENTAS CON CACHE ====================

/**
 * Hook principal para obtener el reporte general de ventas
 * Optimizado para trabajar con el cache del backend
 */
export const useFetchReporteGeneralVentas = (
  params: ReporteGeneralVentasParams,
  options?: {
    enabled?: boolean;
    forceRefresh?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['reporte-general-ventas', params.fecha_desde, params.fecha_hasta, params.id_usuario, params.estado, params.id_ciudad, params.id_vendedor],
    queryFn: async () => {
      return await getReporteGeneralVentas({
        ...params,
        force_refresh: options?.forceRefresh || false
      });
    },
    enabled: options?.enabled !== false && !!params.fecha_desde && !!params.fecha_hasta,
    retry: 1,
    staleTime: 10 * 60 * 1000, // 10 minutos - coincide con el cache del backend
    gcTime: 15 * 60 * 1000, // 15 minutos - mantener en memoria del navegador
    refetchOnWindowFocus: false, // No recargar al volver a la ventana
    refetchOnMount: false, // No recargar al montar el componente si ya hay data
  });
};

/**
 * Función para obtener el reporte general de ventas
 */
export const getReporteGeneralVentas = async (
  params: ReporteGeneralVentasParams & { force_refresh?: boolean }
) => {
  const resp = await post<ReporteGeneralVentasResponse>(
    `${urlBackend}api/v1/obtener-reporte-general-ventas/`,
    params,
    true
  );
  return resp;
};

/**
 * Hook mutation para obtener reporte general de ventas (sin caché automático)
 */
export const useObtenerReporteGeneralVentas = () => {
  return useMutation({
    mutationFn: async (params: ReporteGeneralVentasParams) => {
      const resp = await post<any[]>(
        `${urlBackend}api/v1/obtener-reporte-general-ventas/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook para forzar actualización del reporte (botón "Refrescar")
 */
export const useRefreshReporteGeneralVentas = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ReporteGeneralVentasParams) => {
      return await getReporteGeneralVentas({
        ...params,
        force_refresh: true
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidar la query específica para forzar re-fetch
      queryClient.invalidateQueries({
        queryKey: ['reporte-general-ventas', variables.fecha_desde, variables.fecha_hasta]
      });
    },
  });
};

/**
 * Hook para verificar el estado del cache
 */
export const useFetchEstadoCache = () => {
  return useQuery({
    queryKey: ['estado-cache-reporte-ventas'],
    queryFn: async () => {
      const resp = await get<{
        code: number;
        status: string;
        cache_activo: boolean;
        metadata: {
          ultima_actualizacion?: string;
          fecha_desde?: string;
          fecha_hasta?: string;
          total_registros?: number;
        };
      }>(
        `${urlBackend}api/v1/graficos/estado-cache/`,
        true
      );
      return resp;
    },
    refetchInterval: 60000, // Actualizar cada minuto
    retry: 1,
  });
};

/**
 * Hook para pre-cargar cache de rangos comunes
 */
export const usePrecargaCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const resp = await post<{
        code: number;
        status: string;
        message: string;
        rangos: Array<{ nombre: string }>;
      }>(
        `${urlBackend}api/v1/graficos/precarga-cache/`,
        {},
        true
      );
      return resp;
    },
    onSuccess: () => {
      // Invalidar todas las queries de reportes para que se recarguen
      queryClient.invalidateQueries({
        queryKey: ['reporte-general-ventas']
      });
    },
  });
};

/**
 * Hook para limpiar el cache
 */
export const useLimpiarCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const resp = await post<{
        code: number;
        status: string;
        message: string;
      }>(
        `${urlBackend}api/v1/graficos/limpiar-cache/`,
        {},
        true
      );
      return resp;
    },
    onSuccess: () => {
      // Limpiar queries locales también
      queryClient.invalidateQueries({
        queryKey: ['reporte-general-ventas']
      });
      queryClient.invalidateQueries({
        queryKey: ['estado-cache-reporte-ventas']
      });
    },
  });
};

/**
 * Hook personalizado para manejar el reporte con indicadores de cache
 */
export const useReporteGeneralVentasConCache = (params: ReporteGeneralVentasParams) => {
  const queryClient = useQueryClient();

  const reporteQuery = useFetchReporteGeneralVentas(params);
  const estadoCacheQuery = useFetchEstadoCache();
  const refreshMutation = useRefreshReporteGeneralVentas();
  const limpiarCacheMutation = useLimpiarCache();

  const forceRefresh = async () => {
    await refreshMutation.mutateAsync(params);
  };

  const limpiarCache = async () => {
    await limpiarCacheMutation.mutateAsync();
  };

  // Información del cache de la respuesta
  const cacheInfo = {
    fromCache: reporteQuery.data?.data?.from_cache || false,
    cacheStatus: reporteQuery.data?.data?.cache_status || 'unknown',
    metadata: reporteQuery.data?.data?.metadata || {},
    ultimaActualizacion: reporteQuery.data?.data?.metadata?.ultima_actualizacion || null,
  };

  return {
    // Data del reporte
    data: reporteQuery.data,
    isLoading: reporteQuery.isLoading,
    isError: reporteQuery.isError,
    error: reporteQuery.error,

    // Estado del cache
    cacheInfo,
    estadoCache: estadoCacheQuery.data,

    // Acciones
    forceRefresh,
    limpiarCache,
    isRefreshing: refreshMutation.isPending,
    isLimpiandoCache: limpiarCacheMutation.isPending,

    // Query client para operaciones manuales
    queryClient,
  };
};

/**
 * Hook para obtener filtros de canales y ciudades
 */
export const useObtenerFiltrosVentas = () => {
  return useQuery({
    queryKey: ['filtros-ventas'],
    queryFn: async () => {
      const resp = await get<{
        code: number;
        status: string;
        data: {
          canales: string[];
          ciudades: string[];
        };
      }>(
        `${urlBackend}api/v1/obtener-filtros-ventas/`,
        true
      );
      return resp;
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
};
